import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useId, useRef, useState } from "react";

import {
  atajosPeriodo,
  esFechaISO,
  mesEnPalabras,
  mismoPeriodo,
  moverMes,
  rangoDelPeriodo,
  rangoInvalido,
  rotuloPeriodo,
  type Periodo,
} from "@/lib/periodo";
import { cn } from "@/lib/utils";

import { Button } from "./button";
import { IconButton } from "./icon-button";
import { Input } from "./input";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface SelectorPeriodoProps {
  valor: Periodo;
  /** Controlado: el periodo lo guarda la pantalla, aca adentro solo vive el borrador del rango. */
  onChange: (periodo: Periodo) => void;
  /**
   * `auto` (default): el mes largo desde 1280px (`xl`) y el abreviado abajo, para
   * que la barra de filtros no se parta en dos renglones. `completo` y `corto`
   * lo fijan cuando la pantalla sabe mejor que el breakpoint cuanto lugar hay.
   */
  rotulo?: "auto" | "completo" | "corto";
  /**
   * Apagado: sigue andando pero se ve tenue. Ventas lo usa mientras hay texto en
   * el buscador, porque ahi la busqueda ignora el periodo y hay que explicar por
   * que aparecen comprobantes de otro mes.
   */
  atenuado?: boolean;
  disabled?: boolean;
  /** Reloj. Se inyecta en los tests para congelar "hoy"; en produccion no se pasa. */
  hoy?: Date;
  /** Nombre accesible del grupo. */
  etiqueta?: string;
  className?: string;
}

const MENSAJE_INVALIDO = "El «desde» no puede ser posterior al «hasta».";

/**
 * Selector de periodo: el cuarto control de la barra de filtros (§1.4 de
 * `docs/normalizacion-ui/06-patrones-pantalla.md`, que hasta hoy definia tres).
 *
 * ```
 * [◀  SEPTIEMBRE 2026  ▶ ⌄]
 * ```
 *
 * Las flechas mueven de mes SIN abrir nada: cerrar el mes es el gesto frecuente
 * y tiene que costar un click, no dos. El `⌄` abre el popover con los atajos y
 * el rango libre, que es el gesto raro.
 *
 * Es controlado a proposito: el periodo es estado de pantalla (viaja a la query,
 * y probablemente a la URL), no estado del control. Aca adentro solo vive el
 * borrador de los dos campos de fecha mientras el popover esta abierto.
 */
export function SelectorPeriodo({
  valor,
  onChange,
  rotulo = "auto",
  atenuado = false,
  disabled = false,
  hoy,
  etiqueta = "Período",
  className,
}: SelectorPeriodoProps): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const idDesde = useId();
  const idHasta = useId();
  const idError = useId();
  const refChevron = useRef<HTMLButtonElement>(null);

  const ahora = hoy ?? new Date();
  const anterior = moverMes(valor, -1, ahora);
  const siguiente = moverMes(valor, 1, ahora);
  const completo = rotuloPeriodo(valor, "completo");
  const corto = rotuloPeriodo(valor, "corto");

  /**
   * Al abrir, los campos arrancan mostrando el periodo vigente: si hoy filtra
   * septiembre, el rango libre se edita desde el 01/09 y no desde vacio. Se
   * siembra aca, en el mismo evento que abre, y no en un efecto: un efecto
   * volveria a pisar el borrador cada vez que cambie `valor`, y encima
   * escribiria estado en el render siguiente al de la apertura.
   */
  function abrirOCerrar(abrir: boolean): void {
    if (abrir) {
      const r = rangoDelPeriodo(valor);
      setDesde(r.desde ?? "");
      setHasta(r.hasta ?? "");
    }
    setAbierto(abrir);
  }

  const invalido = rangoInvalido(desde, hasta);
  const completoElRango = esFechaISO(desde) && esFechaISO(hasta);

  function aplicarRango(): void {
    // El backend contesta 400 con el rango dado vuelta; el error se ataja aca
    // para no gastar un viaje en algo que ya se sabe mal.
    if (!completoElRango || invalido) return;
    onChange({ tipo: "rango", desde, hasta });
    setAbierto(false);
  }

  function elegir(periodo: Periodo): void {
    onChange(periodo);
    setAbierto(false);
  }

  return (
    <Popover open={abierto} onOpenChange={abrirOCerrar}>
      <div
        role="group"
        aria-label={etiqueta}
        className={cn(
          // El alto lo dan los items, como en `Segmented`: el conjunto mide 36/32
          // contando el borde y el p-0.5, asi se alinea con las solapas de al lado.
          "inline-flex items-center gap-0.5 rounded-md border border-input bg-background p-0.5",
          atenuado && "opacity-60",
          className,
        )}
      >
        <IconButton
          icono={ChevronLeft}
          tamano="sm"
          disabled={disabled}
          // "Anterior" a secas obliga a leer el rotulo de al lado para saber a
          // donde lleva; el lector de pantalla no lo tiene a la vista.
          aria-label={`Ir a ${mesEnPalabras(anterior)}`}
          onClick={() => onChange(anterior)}
          className="size-8 sm:size-7"
        />

        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label={`${etiqueta}: ${completo}`}
            className="h-8 min-w-24 justify-center px-2 font-medium tabular-nums tracking-wide sm:h-7"
          >
            {/* El texto es decorativo: el nombre accesible es el aria-label, que
                dice el mes largo aunque en pantalla se lea "SEP 26". */}
            {rotulo === "auto" ? (
              <>
                <span className="hidden xl:inline" aria-hidden="true">
                  {completo}
                </span>
                <span className="xl:hidden" aria-hidden="true">
                  {corto}
                </span>
              </>
            ) : (
              <span aria-hidden="true">{rotulo === "corto" ? corto : completo}</span>
            )}
          </Button>
        </PopoverTrigger>

        <IconButton
          icono={ChevronRight}
          tamano="sm"
          disabled={disabled}
          aria-label={`Ir a ${mesEnPalabras(siguiente)}`}
          onClick={() => onChange(siguiente)}
          className="size-8 sm:size-7"
        />

        {/* El ⌄ hace exactamente lo mismo que el rotulo, que ya es el disparador
            del popover. Es una pista visual para el mouse, no un control mas:
            oculto para el lector de pantalla y fuera del orden de tabulacion,
            asi no se anuncia dos veces el mismo boton. */}
        <button
          ref={refChevron}
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          disabled={disabled}
          onClick={() => abrirOCerrar(!abierto)}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50 sm:size-6"
        >
          <ChevronDown className="size-4" />
        </button>
      </div>

      <PopoverContent
        align="end"
        className="w-80 space-y-4"
        aria-label="Elegir el período"
        onInteractOutside={(e) => {
          // El ⌄ vive afuera del panel: sin esto, su click cerraria por
          // "interaccion de afuera" y su onClick volveria a abrir, parpadeando.
          if (refChevron.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Atajos</p>
          <div className="flex flex-wrap gap-2">
            {atajosPeriodo(ahora).map((a) => (
              <Button
                key={a.id}
                type="button"
                size="sm"
                variant={mismoPeriodo(a.periodo, valor) ? "secondary" : "ghost"}
                aria-pressed={mismoPeriodo(a.periodo, valor)}
                onClick={() => elegir(a.periodo)}
              >
                {a.etiqueta}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2 border-t border-border pt-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Rango</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor={idDesde}>Desde</Label>
              <Input
                id={idDesde}
                type="date"
                value={desde}
                aria-invalid={invalido || undefined}
                aria-describedby={invalido ? idError : undefined}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={idHasta}>Hasta</Label>
              <Input
                id={idHasta}
                type="date"
                value={hasta}
                aria-invalid={invalido || undefined}
                aria-describedby={invalido ? idError : undefined}
                onChange={(e) => setHasta(e.target.value)}
              />
            </div>
          </div>
          {invalido ? (
            <p id={idError} role="alert" className="text-xs text-destructive">
              {MENSAJE_INVALIDO}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              El «hasta» entra en el resultado: hasta el 15 incluye el 15.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            className="w-full"
            disabled={!completoElRango || invalido}
            onClick={aplicarRango}
          >
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
